import { useEffect, useRef, useState } from 'react'
import type { TrackSnapshot } from '@/entities/call'
import { cn } from '@/shared/lib'
import { Avatar } from '@/shared/ui/avatar'
import { Icon } from '@/shared/ui/icon'

// Safari < 16.4 умеет fullscreen только с webkit-префиксом.
function requestFullscreen(el: HTMLElement): void {
  const prefixed = el as HTMLElement & { webkitRequestFullscreen?: () => Promise<void> }
  if (el.requestFullscreen) void el.requestFullscreen()
  else if (prefixed.webkitRequestFullscreen) void prefixed.webkitRequestFullscreen()
}

function exitFullscreen(): void {
  const doc = document as Document & { webkitExitFullscreen?: () => Promise<void> }
  if (document.exitFullscreen) void document.exitFullscreen()
  else if (doc.webkitExitFullscreen) void doc.webkitExitFullscreen()
}

interface VideoTileProps {
  name: string
  // Конкретный видеотрек для показа (камера или демонстрация); null → аватар.
  track: TrackSnapshot | null
  isLocal: boolean
  active: boolean
  audioMuted: boolean
  screen?: boolean
}

export function VideoTile({
  name,
  track,
  isLocal,
  active,
  audioMuted,
  screen = false,
}: VideoTileProps) {
  const ref = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [fullscreen, setFullscreen] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el || !track) return
    track.attach(el)
    return () => track.detach(el)
  }, [track])

  useEffect(() => {
    if (!screen) return
    const onChange = () => setFullscreen(document.fullscreenElement === containerRef.current)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [screen])

  const toggleFullscreen = () => {
    const el = containerRef.current
    if (!el) return
    if (document.fullscreenElement) exitFullscreen()
    else requestFullscreen(el)
  }

  return (
    <div
      ref={containerRef}
      className="relative h-full w-full overflow-hidden rounded-card border border-border-default bg-bg-sunken"
    >
      {track ? (
        // Живой webrtc-поток не имеет caption-трека — правило неприменимо.
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          ref={ref}
          autoPlay
          playsInline
          // Свою камеру глушим (эхо); у демонстрации своего аудио-трека нет.
          muted={isLocal || screen}
          // Ничего не кропим (object-contain): и демонстрация, и камера видны
          // целиком в исходных пропорциях, поля добирает тёмный фон тайла.
          // Своя камера зеркалится (только отображение, поток не трогаем) —
          // привычное «как в зеркале»; демонстрацию не зеркалим никогда.
          className={cn('h-full w-full object-contain', isLocal && !screen && '-scale-x-100')}
        />
      ) : (
        // Камера выключена: нейтральный плейсхолдер — мягкий градиент + аватар,
        // тайл держит место в сетке и не даёт раскладке прыгать.
        // pb-9 — высота нижней плашки с именем: аватар центрируется в свободной
        // зоне и не наезжает на имя; на широких тайлах аватар крупнее (@container).
        <div className="@container flex h-full w-full items-center justify-center bg-gradient-to-br from-bg-surface via-bg-sunken to-bg-sunken pb-9">
          <Avatar name={name} className="@[20rem]:h-16 @[20rem]:w-16 @[20rem]:text-lg" />
        </div>
      )}
      {screen && (
        <button
          type="button"
          aria-label={fullscreen ? 'Свернуть демонстрацию' : 'Демонстрация на весь экран'}
          title={fullscreen ? 'Свернуть демонстрацию' : 'Демонстрация на весь экран'}
          onClick={toggleFullscreen}
          className="absolute top-2 right-2 rounded-pill bg-black/50 p-2 text-white transition-colors hover:bg-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
        >
          <Icon name={fullscreen ? 'collapse' : 'expand'} size={18} />
        </button>
      )}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/60 to-transparent px-3 py-2">
        <span className="truncate font-mono text-sm text-text-primary">
          {screen ? `Демонстрация · ${name}` : isLocal ? `${name} · Вы` : name}
        </span>
        {!screen && audioMuted && (
          <Icon name="mic-off" size={16} className="shrink-0 text-text-tertiary" />
        )}
      </div>
      {/* Индикатор говорящего — inset-кольцо поверх видео/градиента внутри
          скругления: не режется overflow-контейнерами и не перекрывается. */}
      {active && (
        <div className="pointer-events-none absolute inset-0 rounded-card ring-2 ring-inset ring-border-accent" />
      )}
    </div>
  )
}
